"use client";

import Comments from "@/components/comments/comments";
import { useAuth } from "@/context/auth-context";
import { useApi } from "@/hooks/useApi";
import { useTrackVisit } from "@/hooks/useTrackVisit";
import { getNews } from "@/lib/api/news/info";
import { BProgress } from "@bprogress/core";
import parse from "html-react-parser";
import JalaliMoment from "jalali-moment";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { CiHeart, CiShare2 } from "react-icons/ci";
import { FaRegCommentDots } from "react-icons/fa6";
import useSWR from "swr";
import { useToast } from "../../hooks/useToast";
import Tags from "../item-info/tags/tags";

export default function NewsInfo({
	initialNews,
	kind,
}: {
	initialNews: any;
	kind: any;
}) {
	const { user } = useAuth();

	const {
		data: news,
		mutate,
		isLoading: isNewsLoading,
	} = useSWR(["news", initialNews?.id], () => getNews(initialNews?.id), {
		fallbackData: initialNews,
		revalidateOnMount: false,
	});

	useTrackVisit("NEWS", initialNews?.id);

	const contentRef = useRef<any>(null);
	const backgroundRef = useRef<any>(null);
	const [liked, setLiked] = useState(false);
	const { addToast } = useToast();
	const { call } = useApi();
	const { call: getLikeUser } = useApi();

	useEffect(() => {
		const fetchUserScore = async () => {
			try {
				const res = await getLikeUser(
					`/api/news/like/${news.id}`,
					{
						method: "GET",
						headers: {
							"Content-Type": "application/json",
						},
					},
					false,
				);
				const data = res;
				setLiked(data?.marked);
			} catch (error) {
				console.error("Failed to fetch user score:", error);
				setLiked(false);
			}
		};

		if (news && news.id) {
			fetchUserScore();
		}
	}, [news, news?.id]);

	const handleShare = async () => {
		try {
			await navigator.clipboard.writeText(window.location.href);
			addToast("لینک صفحه کپی شد", "info");
		} catch (err) {
			addToast("خطا در کپی کردن لینک", "error");
			console.error("Failed to copy text: ", err);
		}
	};

	const updateBackgroundHeight = () => {
		if (contentRef.current && backgroundRef.current) {
			const contentHeight = contentRef.current?.offsetHeight;
			backgroundRef.current.style.height = `${contentHeight}px`;
		}
	};

	const like = async () => {
		if (liked) {
			addToast(
				"این خبر پیش‌تر به فهرست علاقه‌مندی‌های شما افزوده شده است.",
				"info",
			);
			return;
		}
		if (!user) {
			addToast("ابتدا وارد شوید", "info");
			return;
		}
		await call(
			`/api/news/like`,
			{
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ newsId: news.id }),
			},
			true,
		);
		setLiked(!liked);
		await mutate();
	};

	useEffect(() => {
		if (isNewsLoading) {
			BProgress.start();
		} else {
			BProgress.done();
		}
	}, [isNewsLoading]);

	// Set initial height and add resize listener
	useEffect(() => {
		// Initial height adjustment
		updateBackgroundHeight();

		// Update height on window resize
		window.addEventListener("resize", updateBackgroundHeight);

		// Clean up
		return () => {
			window.removeEventListener("resize", updateBackgroundHeight);
		};
	}, []);

	const jalaliDate = JalaliMoment(news?.createdAt)
		.locale("fa")
		.format("YYYY/MM/DD");
	const timeHoursMinutes = JalaliMoment(news?.createdAt).format("HH:mm");

	return (
		<>
			<div
				ref={backgroundRef}
				className="absolute -z-10 w-full top-[-148px]"
				style={{
					backgroundImage: `url(${
						process.env.NEXT_PUBLIC_FILE_URL + news.posterImage?.code
					}),
                      linear-gradient(to bottom, transparent 80%, rgba(0, 0, 0, 1)),
                      linear-gradient(to right, transparent 30%, rgba(0, 0, 0, .6)),
                      linear-gradient(to left, transparent 30%, rgba(0, 0, 0, .6))
                    `,
					backgroundRepeat: "no-repeat",
					backgroundSize: "cover",
					backgroundPosition: "top",
					backgroundBlendMode: "overlay",
				}}
			></div>
			<div className="w-full text-white py-6 px-6 lg:px-[80px] flex flex-col lg:flex items-start mt-20 gap-8">
				<div ref={contentRef}>
					<div className="mt-96 w-full flex flex-col-reverse md:flex-row-reverse gap-4">
						<div
							className={
								"w-full md:w-[calc(100%-60px)] py-6 flex flex-col gap-4"
							}
						>
							<section id="header" className="mb-4">
								<h1 className="text-[35px] md:text-[48px] font-extraBold">
									{news?.title}
								</h1>
								<p>
									{`${jalaliDate} ساعت ${timeHoursMinutes}`} | {`56.4K بازدید`}{" "}
									|{" "}
									{news?.comments?.length
										? `${news?.comments?.length} دیدگاه ها |`
										: ""}{" "}
									{`زمان مطالعه ${news?.readingTime} دقیقه`}
								</p>
							</section>
							<section className="font-normal text-white" id="content">
								{parse(news?.newsContent)}
							</section>
						</div>
						<div className="w-full md:w-[56px] py-9 flex flex-row md:flex-col items-center gap-9">
							<div
								className={`flex gap-2 items-center ${
									liked ? "text-red-600" : "text-white"
								} cursor-pointer hover:text-red-600`}
							>
								{news.countOfLike ?? 0}
								<CiHeart size={28} onClick={like} />
							</div>
							<Link
								href={"#comments"}
								className={
									"flex gap-2 items-center text-white hover:text-blue-400 cursor-pointer"
								}
							>
								{news?.comments && news?.comments?.length
									? news?.comments?.length
									: 0}
								<FaRegCommentDots size={24} />
							</Link>
							<button
								className={`flex gap-2 items-center ${"text-white"} cursor-pointer hover:text-purple-500`}
								onClick={() => handleShare()}
							>
								<CiShare2 size={28} />
							</button>
						</div>
					</div>
					<div className="px-20">
						{news.tags && <Tags tags={news.tags} kind={kind} news={true} />}
					</div>
				</div>
				<div className={"w-full py-[64px]"}>
					<Comments
						comments={news?.comments ?? []}
						refresh={mutate}
						user={user}
						itemId={news?.id}
						type="NEWS"
					/>
				</div>
			</div>
		</>
	);
}
